/**
 * Verify Email Page
 * Handles Firebase email verification links (mode=verifyEmail)
 */

import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { auth } from '@/config/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { applyActionCode, checkActionCode } from 'firebase/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import toast from 'react-hot-toast';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const { sendVerificationEmail, currentUser } = useAuth();

  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');

  useEffect(() => {
    let active = true;
    async function run() {
      if (!oobCode || mode !== 'verifyEmail') {
        setError('Invalid verification link.');
        setVerifying(false);
        return;
      }
      try {
        // Optional: get email info for nicer UX
        try {
          const info = await checkActionCode(auth, oobCode);
          const userEmail = info?.data?.email;
          if (userEmail && active) setEmail(userEmail);
        } catch (_) {}

        await applyActionCode(auth, oobCode);
        if (!active) return;
        setDone(true);
        toast.success('Email verified successfully');
        // slight delay, then navigate to sign in (or dashboard if already logged in)
        setTimeout(() => navigate('/auth/signin', { replace: true }), 800);
      } catch (e) {
        console.error('applyActionCode error', e);
        setError('This verification link is invalid or has expired.');
      } finally {
        if (active) setVerifying(false);
      }
    }
    run();
    return () => { active = false; };
  }, [oobCode, mode, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 py-12 px-4 sm:px-6 lg:px-8 transition-colors">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">FinList</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Verify your email</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Email Verification</CardTitle>
            <CardDescription>
              {verifying ? 'Validating your verification link…' : (done ? 'Email verified' : 'Unable to validate link')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {verifying ? (
              <div className="text-sm text-gray-600">Please wait…</div>
            ) : error && !done ? (
              <div className="space-y-4">
                <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>
                <div className="flex items-center justify-between">
                  <Link to="/auth/signin" className="text-blue-600 hover:text-blue-500 text-sm">Back to sign in</Link>
                  {currentUser && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async ()=>{
                        try { await sendVerificationEmail(); } catch (_) {}
                      }}
                    >
                      Resend verification
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-md bg-green-50 p-4 text-sm text-green-700">
                  {email ? `Your email ${email} has been verified.` : 'Your email has been verified.'}
                </div>
                <Link to="/auth/signin">
                  <Button className="w-full">Continue to Sign In</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
